import {
  Avatar,
  Checkbox,
  TableCell,
  TableRow, Tooltip,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { EmptyAvatar } from '../../../../components/common/avatars';
import { CustomTooltip } from '../../../../components/CustomTooltip';
import { Linkedin } from '../../../../icons/linkedin';
import { CandidateMatchesActions } from '../CandidateMatchesActions';
import { CandidatePromoteStateError } from './CandidatePromoteStateError';
import { CandidatePromoteStateFetching } from './CandidatePromoteStateFetching';
import { CandidatePendingPromotion } from './CandidatePendingPromotion';
import { style } from './style';

type CandidateMatchItemProps = {
  item: MatchCandidateResponse,
  onAcceptClick: () => void,
  onRejectClick: () => void,
  isSelected: boolean,
  onSelectClick: () => void,
  promoteErrorMessage: string,
  isPromoting: boolean,
  isPendingPromotion: boolean,
}

export const CandidateMatchItem = ({
  item,
  onRejectClick,
  onAcceptClick,
  isSelected,
  onSelectClick,
  promoteErrorMessage,
  isPromoting,
  isPendingPromotion,
}: CandidateMatchItemProps)
:JSX.Element => {
  const [isRowHovered, setIsRowHovered] = useState<boolean>(false);

  const handleRowHover = () => {
    setIsRowHovered(true);
  };

  const handleRowUnHover = () => {
    setIsRowHovered(false);
  };

  const renderPromote = () => {
    if (isPendingPromotion) {
      return <CandidatePendingPromotion />;
    }
    if (isPromoting) {
      return <CandidatePromoteStateFetching />;
    }

    if (promoteErrorMessage) {
      return <CandidatePromoteStateError promoteErrorMessage={ promoteErrorMessage } />;
    }

    return null;
  };

  return (
    <TableRow
      key={ item.id }
      sx={ {
        ...style.tableRow.sx(
          isPromoting || isPendingPromotion,
          isRowHovered),
      } }
      onMouseEnter={ handleRowHover }
      onMouseLeave={ handleRowUnHover }
    >
      <TableCell>
        <Checkbox
          checked={ isSelected }
          onChange={ onSelectClick }
        />
      </TableCell>
      <TableCell sx={ style.cellId.sx }>
        <Box sx={ style.boxMatch.sx }>
          <Avatar
            variant={ style.avatar.variant }
            sx={ style.avatar.sx }
            src={ item.profileImage }
          >
            <EmptyAvatar />
          </Avatar>
          <Box>
            <Box sx={ style.boxMatch.sx }>
              <CustomTooltip title={ `${item.firstName} ${item.lastName}` }>
                <Typography
                  variant="body1"
                >
                  { item.firstName} {item.lastName}
                </Typography>
              </CustomTooltip>
              {!!item.fullLinkedinUrl && (
                <a style={ style.link }
                  target="_blank"
                  href={ item.fullLinkedinUrl }
                  rel="noreferrer"
                >
                  <Linkedin viewBox="0 0 18 18" sx={ style.link } />
                </a>
              )}
            </Box>
            <CustomTooltip title={ `${item?.location?.city} ${item?.location?.country}` }>
              <Typography
                variant="caption"
                sx={ style.typography.sx }
              >
                { item?.location?.city} { item?.location?.country }
              </Typography>
            </CustomTooltip>
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={ style.cellPosition.sx }>
        <Box sx={ style.cell.sx }>
          {item?.currentJob?.jobTitle && (
            <CustomTooltip title={ item?.currentJob?.jobTitle }>
              <Typography
                variant="body1"
              >
                { item?.currentJob.jobTitle}
              </Typography>
            </CustomTooltip>
          )}
          {item?.currentJob?.companyName && (
            <CustomTooltip title={ item.currentJob?.companyName }>
              <Typography
                variant="body2"
                sx={ style.typography.sx }
              >
                { item?.currentJob?.companyName}
              </Typography>
            </CustomTooltip>
          )}
        </Box>
      </TableCell>
      <TableCell sx={ style.cellRank.sx }>
        <Typography
          variant="h6"
        >
          {item?.matchingRank}
        </Typography>
      </TableCell>
      <TableCell sx={ style.cellEQ.sx }>
        <Tooltip title={ item?.talentSimilarityScore }>
          <Typography
            variant="h6"
          >
            {item?.talentSimilarityScore?.toFixed(2)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={ style.cellWtm.sx }>
        <Tooltip title={ item?.wtlScore }>
          <Typography
            variant="h6"
          >
            {item?.wtlScore?.toFixed(2)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={ style.cellWtm.sx }>
        <Tooltip title={ item?.layoffScore }>
          <Typography
            variant="h6"
          >
            {item?.layoffScore}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={ style.cellWtm.sx }>
        <Tooltip title={ item?.talentSemanticMinScore }>
          <Typography
            variant="h6"
          >
            { item.talentSemanticMinScore?.toFixed(2) }
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={ style.cellWtm.sx }>
        <Tooltip title={ item?.talentSemanticAboveMinCount }>
          <Typography
            variant="h6"
          >
            { item.talentSemanticAboveMinCount?.toFixed(2) }
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell
        sx={ style.candidateActions.sx }
        style={ style.unDisable }
      >
        { renderPromote() }
        { isRowHovered && (
          <CandidateMatchesActions
            onAcceptClick={ onAcceptClick }
            onRejectClick={ onRejectClick }
          />
        )}
      </TableCell>
    </TableRow>
  );
};
